const twoShirtGa4Bridge = window.__lexiboTwoShirtGa4Bridge || (() => {
  const measurementId = 'G-SP7RV0ZSQ6';
  const ga4EventNames = new Set([
    'two_shirt_picker_view',
    'two_shirt_size_selected',
    'two_shirt_selection_complete',
    'two_shirt_size_mode_changed',
    'two_shirt_add_clicked',
    'two_shirt_add_succeeded',
    'two_shirt_add_failed',
  ]);
  const developmentThemeRoles = new Set(['development', 'unpublished']);
  const pendingGa4Events = [];
  let analyticsAllowed = false;
  let analyticsWasEnabled = false;

  const clonePayload = (payload) => ({
    ...payload,
    attribution: { ...(payload.attribution || {}) },
    ...(Array.isArray(payload.items) ? { items: payload.items.map((item) => ({ ...item })) } : {}),
  });

  const initializeGoogleTag = () => {
    if (window.__lexiboTwoShirtGoogleTagInitialized) return;

    window.__lexiboTwoShirtGoogleTagInitialized = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
      window.dataLayer.push(arguments);
    };

    window.gtag('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    const existingGoogleTag = document.querySelector(
      `script[src*="googletagmanager.com/gtag/js"][src*="${measurementId}"]`
    );
    if (!existingGoogleTag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      script.dataset.lexiboTwoShirtGa4 = '';
      document.head.appendChild(script);
    }
  };

  const sendToGa4 = (payload) => {
    if (!analyticsAllowed || !ga4EventNames.has(payload.event)) return;

    initializeGoogleTag();
    const { event, attribution = {}, ...parameters } = payload;
    const themeRole = window.Shopify?.theme?.role;
    const ga4Parameters = {
      ...parameters,
      ...attribution,
      send_to: measurementId,
      ...(developmentThemeRoles.has(themeRole) ? { debug_mode: true } : {}),
    };

    if (Array.isArray(ga4Parameters.sizes)) {
      ga4Parameters.sizes = ga4Parameters.sizes.join('|');
    }

    window.gtag('event', event, ga4Parameters);
  };

  const track = (payload) => {
    if (!ga4EventNames.has(payload.event)) return;

    if (analyticsAllowed) {
      sendToGa4(payload);
    } else if (!analyticsWasEnabled) {
      pendingGa4Events.push(clonePayload(payload));
    }
  };

  const enableAnalytics = () => {
    if (analyticsAllowed) return;

    analyticsAllowed = true;
    analyticsWasEnabled = true;
    initializeGoogleTag();
    pendingGa4Events.splice(0).forEach(sendToGa4);
  };

  const disableAnalytics = () => {
    const wasAllowed = analyticsAllowed;
    analyticsAllowed = false;

    if (analyticsWasEnabled) pendingGa4Events.splice(0);
    if (wasAllowed && window.__lexiboTwoShirtGoogleTagInitialized) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }
  };

  const updateAnalyticsConsent = () => {
    const customerPrivacy = window.Shopify?.customerPrivacy;
    if (!customerPrivacy) return;

    if (customerPrivacy.analyticsProcessingAllowed()) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }
  };

  const initializePrivacy = () => {
    if (window.Shopify?.customerPrivacy) {
      updateAnalyticsConsent();
      return;
    }

    if (!window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) updateAnalyticsConsent();
    });
  };

  document.addEventListener('visitorConsentCollected', updateAnalyticsConsent);
  initializePrivacy();

  return { track };
})();

window.__lexiboTwoShirtGa4Bridge = twoShirtGa4Bridge;

if (!customElements.get('two-shirt-picker')) {
  customElements.define(
    'two-shirt-picker',
    class TwoShirtPicker extends HTMLElement {
      constructor() {
        super();
        this.mode = 'shared';
        this.sharedSize = null;
        this.separateSizes = { manta: null, hammerhead: null };
        this.liveUnavailable = new Set();
        this.isSubmitting = false;
      }

      connectedCallback() {
        const payload = this.querySelector('[data-picker-products]');
        if (!payload) return;

        try {
          const data = JSON.parse(payload.textContent);
          this.currency = data.currency || 'EUR';
          this.products = data.products.map((product) => this.normalizeProduct(product));
        } catch (error) {
          console.error('Zwei-Shirt-Picker konnte nicht initialisiert werden.', error);
          return;
        }

        this.sharedPicker = this.querySelector('[data-shared-picker]');
        this.separatePicker = this.querySelector('[data-separate-picker]');
        this.modeToggle = this.querySelector('[data-mode-toggle]');
        this.submitButton = this.querySelector('[data-add-both]');
        this.ctaLabel = this.querySelector('[data-cta-label]');
        this.message = this.querySelector('[data-picker-message]');
        this.successDrawer = this.querySelector('[data-success-drawer]');
        this.drawerOverlay = this.querySelector('[data-drawer-overlay]');

        this.modeToggle?.addEventListener('click', () => this.toggleMode());
        this.submitButton?.addEventListener('click', () => this.addBoth());
        this.querySelectorAll('[data-drawer-close], [data-drawer-overlay]').forEach((button) => {
          button.addEventListener('click', () => this.closeDrawer());
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !this.successDrawer?.hidden) this.closeDrawer();
        });

        this.render();
        this.captureAttribution();
        this.track('two_shirt_picker_view');
      }

      normalizeProduct(product) {
        const colorIndex = product.options.findIndex((name) => /farbe|color|colour/i.test(name));
        const sizeIndex = product.options.findIndex((name) => /gr[oö]ße|size/i.test(name));
        const fixedVariants = product.variants.filter((variant) => {
          return colorIndex < 0 || variant.options[colorIndex] === product.fixedColor;
        });

        return {
          ...product,
          colorIndex,
          sizeIndex,
          fixedVariants,
          sizes: [...new Set(fixedVariants.map((variant) => variant.options[sizeIndex]).filter(Boolean))]
            .filter((size) => String(size).trim().toUpperCase() !== '4XL'),
        };
      }

      captureAttribution() {
        const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
        const params = new URLSearchParams(window.location.search);
        const current = {};

        keys.forEach((key) => {
          const value = params.get(key);
          if (value) current[key] = value.slice(0, 200);
        });

        try {
          const saved = JSON.parse(sessionStorage.getItem('lexibo_two_shirt_utm') || '{}');
          this.attribution = { ...saved, ...current };
          if (Object.keys(this.attribution).length) {
            sessionStorage.setItem('lexibo_two_shirt_utm', JSON.stringify(this.attribution));
          }
        } catch (_error) {
          this.attribution = current;
        }
      }

      getProduct(key) {
        return this.products.find((product) => product.key === key);
      }

      getVariant(product, size, requireAvailable = true) {
        if (!product || !size) return null;
        return product.fixedVariants.find((variant) => {
          const sizeMatches = variant.options[product.sizeIndex] === size;
          const available = variant.available && !this.liveUnavailable.has(String(variant.id));
          return sizeMatches && (!requireAvailable || available);
        }) || null;
      }

      getAllSizes() {
        const ordered = [];
        this.products.forEach((product) => {
          product.sizes.forEach((size) => {
            if (!ordered.includes(size)) ordered.push(size);
          });
        });
        return ordered;
      }

      isSharedSizeAvailable(size) {
        return this.products.every((product) => Boolean(this.getVariant(product, size)));
      }

      render() {
        this.renderSizeGroup('shared', this.getAllSizes());
        this.products.forEach((product) => this.renderSizeGroup(product.key, product.sizes));
        this.updateState();
      }

      renderSizeGroup(key, sizes) {
        const container = this.querySelector(`[data-size-options="${key}"]`);
        if (!container) return;

        container.replaceChildren(
          ...sizes.map((size) => {
            const button = document.createElement('button');
            const selected = key === 'shared' ? this.sharedSize === size : this.separateSizes[key] === size;
            const available = key === 'shared'
              ? this.isSharedSizeAvailable(size)
              : Boolean(this.getVariant(this.getProduct(key), size));

            button.type = 'button';
            button.className = 'two-shirt-picker__size';
            button.textContent = size;
            button.dataset.size = size;
            button.dataset.productKey = key;
            button.setAttribute('role', 'radio');
            button.setAttribute('aria-checked', selected ? 'true' : 'false');
            button.disabled = !available;
            if (!available) button.setAttribute('aria-label', `${size}, nicht verfügbar`);
            button.addEventListener('click', () => this.selectSize(key, size));
            return button;
          })
        );
      }

      selectSize(key, size) {
        this.clearErrors();
        if (key === 'shared') {
          this.sharedSize = size;
          this.track('two_shirt_size_selected', { mode: 'shared', size });
        } else {
          this.separateSizes[key] = size;
          this.track('two_shirt_size_selected', { mode: 'separate', product: key, size });
        }
        this.render();

        if (this.getSelections().every((selection) => selection.variant)) {
          this.track('two_shirt_selection_complete', {
            mode: this.mode,
            sizes: this.getSelections().map((selection) => selection.size),
          });
        }
      }

      toggleMode() {
        this.clearErrors();
        if (this.mode === 'shared') {
          this.mode = 'separate';
          if (this.sharedSize) {
            this.separateSizes = { manta: this.sharedSize, hammerhead: this.sharedSize };
          }
        } else {
          this.mode = 'shared';
          const { manta, hammerhead } = this.separateSizes;
          this.sharedSize = manta && manta === hammerhead && this.isSharedSizeAvailable(manta) ? manta : null;
        }

        this.sharedPicker.hidden = this.mode !== 'shared';
        this.separatePicker.hidden = this.mode !== 'separate';
        this.modeToggle.setAttribute('aria-expanded', this.mode === 'separate' ? 'true' : 'false');
        this.modeToggle.textContent = this.mode === 'shared'
          ? 'Unterschiedliche Größen wählen'
          : 'Eine Größe für beide wählen';
        this.track('two_shirt_size_mode_changed', { mode: this.mode });
        this.render();
      }

      getSelections() {
        return this.products.map((product) => {
          const size = this.mode === 'shared' ? this.sharedSize : this.separateSizes[product.key];
          return { product, size, variant: this.getVariant(product, size) };
        });
      }

      updateState() {
        const selections = this.getSelections();
        const ready = selections.every((selection) => Boolean(selection.variant));
        const selectedCount = selections.filter((selection) => Boolean(selection.variant)).length;
        const total = selections.reduce((sum, selection) => {
          const fallback = selection.product.fixedVariants[0];
          return sum + Number(selection.variant?.price ?? fallback?.price ?? 0);
        }, 0);

        this.querySelectorAll('[data-selection-total]').forEach((element) => {
          element.textContent = this.formatMoney(total);
        });

        selections.forEach(({ product, size }) => {
          const label = this.querySelector(`[data-product-selection="${product.key}"]`);
          if (label) label.textContent = size ? `Größe ${size}` : 'Größe wählen';
        });

        if (this.isSubmitting) return;
        this.submitButton.disabled = !ready;

        if (ready && this.mode === 'shared') {
          this.ctaLabel.textContent = `Beide in Größe ${this.sharedSize} in den Warenkorb · ${this.formatMoney(total)}`;
        } else if (ready) {
          this.ctaLabel.textContent = `Beide in den Warenkorb · ${this.formatMoney(total)}`;
        } else if (this.mode === 'shared') {
          this.ctaLabel.textContent = 'Bitte Größe wählen';
        } else if (selectedCount === 0) {
          this.ctaLabel.textContent = 'Bitte zwei Größen wählen';
        } else {
          const missing = selections.find((selection) => !selection.variant);
          this.ctaLabel.textContent = `Noch Größe für ${missing.product.key === 'manta' ? 'Manta' : 'Hammerhead'} wählen`;
        }
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: this.currency,
        }).format(cents / 100);
      }

      async validateLiveAvailability(selections) {
        const results = await Promise.all(
          selections.map(async (selection) => {
            try {
              const response = await fetch(`${selection.product.url}.js`, {
                headers: { Accept: 'application/json' },
              });
              if (!response.ok) return { selection, available: true };
              const product = await response.json();
              const liveVariant = product.variants.find((variant) => String(variant.id) === String(selection.variant.id));
              return { selection, available: Boolean(liveVariant?.available) };
            } catch (_error) {
              return { selection, available: true };
            }
          })
        );

        let valid = true;
        results.forEach(({ selection, available }) => {
          if (available) return;
          valid = false;
          this.liveUnavailable.add(String(selection.variant.id));
          this.showProductError(selection.product.key, 'Diese Größe ist gerade nicht mehr verfügbar. Bitte wähle eine andere.');
        });
        if (!valid) this.render();
        return valid;
      }

      async getCart() {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Warenkorb konnte nicht gelesen werden.');
        return response.json();
      }

      async addBoth() {
        if (this.isSubmitting) return;
        const selections = this.getSelections();
        if (!selections.every((selection) => selection.variant)) return;

        this.clearErrors();
        this.setLoading(true);
        this.track('two_shirt_add_clicked', { items: this.analyticsItems(selections) });

        let cartBefore;
        try {
          const liveAvailable = await this.validateLiveAvailability(selections);
          if (!liveAvailable) return;

          cartBefore = await this.getCart();
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: selections.map(({ variant }) => ({ id: variant.id, quantity: 1 })),
              sections: ['cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          const result = await response.json();
          const returnedIds = new Set((result.items || []).map((item) => String(item.variant_id || item.id)));
          const complete = response.ok && selections.every(({ variant }) => returnedIds.has(String(variant.id)));

          if (!complete) {
            await this.restoreCart(cartBefore, selections.map(({ variant }) => String(variant.id)));
            throw new Error(result.description || 'Die beiden Shirts konnten nicht gemeinsam hinzugefügt werden.');
          }

          const cartAfter = await this.getCart();
          await this.persistAttribution();
          this.updateCartIcon(result.sections?.['cart-icon-bubble']);
          const analyticsPayload = {
            currency: this.currency,
            value: selections.reduce((sum, selection) => sum + Number(selection.variant.price), 0) / 100,
            items: this.analyticsItems(selections),
          };
          this.track('add_to_cart', analyticsPayload);
          this.track('two_shirt_add_succeeded', analyticsPayload);
          this.openDrawer(selections, cartAfter);
        } catch (error) {
          if (cartBefore) {
            try {
              await this.restoreCart(cartBefore, selections.map(({ variant }) => String(variant.id)));
            } catch (rollbackError) {
              console.error('Warenkorb konnte nach dem Fehler nicht vollständig wiederhergestellt werden.', rollbackError);
            }
          }
          this.message.textContent = error.message || 'Das hat gerade nicht geklappt. Bitte versuche es erneut.';
          this.track('two_shirt_add_failed', { message: this.message.textContent });
        } finally {
          this.setLoading(false);
        }
      }

      async restoreCart(snapshot, affectedVariantIds) {
        const current = await this.getCart();
        const snapshotQuantities = new Map();
        snapshot.items.forEach((item) => {
          const id = String(item.variant_id);
          snapshotQuantities.set(id, (snapshotQuantities.get(id) || 0) + item.quantity);
        });

        for (const variantId of affectedVariantIds) {
          const expected = snapshotQuantities.get(variantId) || 0;
          let currentLines = current.items.filter((item) => String(item.variant_id) === variantId);
          let excess = currentLines.reduce((sum, item) => sum + item.quantity, 0) - expected;

          for (const line of currentLines) {
            if (excess <= 0) break;
            const remove = Math.min(excess, line.quantity);
            const response = await fetch(this.dataset.cartChangeUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: line.key, quantity: line.quantity - remove }),
            });
            if (!response.ok) throw new Error('Warenkorb-Rücksetzung fehlgeschlagen.');
            excess -= remove;
          }
        }
      }

      async persistAttribution() {
        if (!this.attribution || !Object.keys(this.attribution).length) return;
        const labels = {
          utm_source: 'Zwei-Shirts UTM Source',
          utm_medium: 'Zwei-Shirts UTM Medium',
          utm_campaign: 'Zwei-Shirts UTM Campaign',
          utm_content: 'Zwei-Shirts UTM Content',
          utm_term: 'Zwei-Shirts UTM Term',
        };
        const attributes = {};
        Object.entries(this.attribution).forEach(([key, value]) => {
          if (labels[key]) attributes[labels[key]] = value;
        });
        if (!Object.keys(attributes).length) return;

        try {
          await fetch(this.dataset.cartUpdateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes }),
          });
        } catch (_error) {
          // Attribution should never block a successful purchase flow.
        }
      }

      updateCartIcon(html) {
        if (!html) return;
        const target = document.getElementById('cart-icon-bubble');
        if (!target) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('.shopify-section') || parsed.body;
        target.innerHTML = source.innerHTML;
      }

      openDrawer(selections, cart) {
        const lines = this.querySelector('[data-success-lines]');
        lines.replaceChildren(
          ...selections.map(({ product, size, variant }) => {
            const row = document.createElement('div');
            row.className = 'two-shirt-picker__drawer-line';

            const image = document.createElement('img');
            const imageUrl = variant.featured_image?.src || variant.featured_image || '';
            if (imageUrl) image.src = imageUrl;
            image.alt = '';
            image.width = 144;
            image.height = 144;

            const copy = document.createElement('div');
            const title = document.createElement('strong');
            title.textContent = product.title;
            const meta = document.createElement('span');
            meta.textContent = `${product.fixedColor} · Größe ${size}`;
            copy.append(title, meta);

            const price = document.createElement('b');
            price.textContent = this.formatMoney(Number(variant.price));
            row.append(image, copy, price);
            return row;
          })
        );
        this.querySelector('[data-cart-total]').textContent = this.formatMoney(Number(cart.total_price));
        this.drawerOverlay.hidden = false;
        this.successDrawer.hidden = false;
        document.body.classList.add('two-shirt-picker-drawer-open');
        this.successDrawer.querySelector('[data-drawer-close]')?.focus();
      }

      closeDrawer() {
        this.drawerOverlay.hidden = true;
        this.successDrawer.hidden = true;
        document.body.classList.remove('two-shirt-picker-drawer-open');
        this.submitButton?.focus();
      }

      setLoading(loading) {
        this.isSubmitting = loading;
        this.submitButton.classList.toggle('is-loading', loading);
        this.submitButton.disabled = loading || !this.getSelections().every((selection) => selection.variant);
        if (loading) this.ctaLabel.textContent = 'Wird hinzugefügt …';
        else this.updateState();
      }

      showProductError(key, text) {
        const error = this.querySelector(`[data-product-error="${key}"]`);
        if (!error) return;
        error.textContent = text;
        error.hidden = false;
      }

      clearErrors() {
        this.message.textContent = '';
        this.querySelectorAll('[data-product-error]').forEach((error) => {
          error.textContent = '';
          error.hidden = true;
        });
      }

      analyticsItems(selections) {
        return selections.map(({ product, size, variant }) => ({
          item_id: String(variant.id),
          product_id: String(product.id),
          item_name: product.title,
          item_variant: `${product.fixedColor} / ${size}`,
          price: Number(variant.price) / 100,
          quantity: 1,
        }));
      }

      track(eventName, detail = {}) {
        const payload = {
          event: eventName,
          two_shirt_picker: true,
          ...detail,
          attribution: this.attribution || {},
        };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        document.dispatchEvent(new CustomEvent('lexibo:two-shirt-picker', { detail: payload }));
        twoShirtGa4Bridge.track(payload);
      }
    }
  );
}
